import { useEffect, useMemo, useState } from 'react';
import { parseUserMoney } from '@/features/money';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { FormDiscount } from './invoiceFormSchema';

interface DiscountDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  initial: FormDiscount | null | undefined;
  currency: string;
  /**
   * `title`/`description` differ between per-line and invoice-level
   * use sites — keep them as props so the dialog stays generic.
   */
  title?: string;
  description?: string;
  /**
   * Saving the dialog passes the new shape (or `null` to clear). The
   * caller is responsible for writing it back into the form.
   */
  onSave: (next: FormDiscount | null) => void;
}

/**
 * Percent-or-fixed discount picker, mirrors the mobile DiscountSheet
 * UX: segmented toggle, large display input, quick-pick chips,
 * Apply / Remove. Value is held as a string while typing (mirrors the
 * trailing-zero bug write-up at mobile/.../DiscountSheet.tsx) and
 * parsed on apply.
 */
export function DiscountDialog({
  open,
  onOpenChange,
  initial,
  currency,
  title = 'Discount',
  description = 'Percentage of the line, or a flat amount in the invoice currency.',
  onSave,
}: DiscountDialogProps) {
  const [type, setType] = useState<'percentage' | 'fixed'>(
    initial?.type ?? 'percentage',
  );
  const [text, setText] = useState<string>(() => initialText(initial));

  useEffect(() => {
    if (open) {
      setType(initial?.type ?? 'percentage');
      setText(initialText(initial));
    }
  }, [open, initial]);

  const quickChips = type === 'percentage' ? ['5', '10', '15', '20'] : ['25', '50', '100', '200'];
  const symbol = currencySymbol(currency);

  const valid = useMemo(() => {
    const trimmed = text.trim();
    if (trimmed === '') return false;
    if (type === 'percentage') {
      if (!/^[0-9]+(?:[.,][0-9]+)?$/.test(trimmed)) return false;
      const n = Number.parseFloat(trimmed.replace(',', '.'));
      return Number.isFinite(n) && n > 0;
    }
    return parseUserMoney(trimmed, currency).ok;
  }, [text, type, currency]);

  function handleSwitch(next: 'percentage' | 'fixed') {
    setType(next);
    // Reset the text so a stale "10" doesn't become "$10" by accident
    // when the user toggles. Default to the same shape mobile uses.
    setText(next === 'percentage' ? '10' : '');
  }

  function handleApply() {
    const trimmed = text.trim();
    if (!valid) return;
    if (type === 'percentage') {
      onSave({ type: 'percentage', rate: trimmed.replace(',', '.') });
    } else {
      onSave({ type: 'fixed', amount: trimmed });
    }
    onOpenChange(false);
  }

  function handleRemove() {
    onSave(null);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 rounded-xl bg-secondary/60 p-1">
          {(['percentage', 'fixed'] as const).map((k) => {
            const on = type === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => handleSwitch(k)}
                className={cn(
                  'flex-1 rounded-lg py-2 text-sm font-medium transition-colors',
                  on
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {k === 'percentage' ? 'Percentage' : 'Fixed amount'}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-center gap-2 rounded-xl border border-line bg-secondary/30 px-4 py-6">
          {type === 'fixed' ? (
            <span className="font-display text-3xl text-muted-foreground">{symbol}</span>
          ) : null}
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            inputMode="decimal"
            autoFocus
            aria-label={type === 'percentage' ? 'Discount percent' : 'Discount amount'}
            className="h-auto w-32 border-transparent bg-transparent text-center font-display text-4xl tabular-nums shadow-none focus-visible:bg-card focus-visible:shadow-sm"
          />
          {type === 'percentage' ? (
            <span className="font-display text-3xl text-muted-foreground">%</span>
          ) : null}
        </div>

        <div className="flex gap-2">
          {quickChips.map((chip) => {
            const on = text.trim() === chip;
            return (
              <button
                key={chip}
                type="button"
                onClick={() => setText(chip)}
                className={cn(
                  'flex-1 rounded-lg py-2 text-sm font-medium transition-colors',
                  on
                    ? 'bg-foreground text-background'
                    : 'bg-secondary/60 text-foreground hover:bg-secondary',
                )}
              >
                {type === 'percentage' ? `${chip}%` : `${symbol}${chip}`}
              </button>
            );
          })}
        </div>

        <DialogFooter>
          {initial ? (
            <Button variant="ghost" onClick={handleRemove}>
              Remove
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={!valid}>
            Apply discount
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function initialText(initial: FormDiscount | null | undefined): string {
  if (!initial) return '10';
  if (initial.type === 'percentage') return initial.rate;
  return initial.amount;
}

/**
 * Tiny Intl.NumberFormat-backed currency-symbol lookup. Returns the
 * ISO code itself (e.g. `BHD`) when Intl can't produce a glyph, which
 * is at least better than rendering nothing.
 */
function currencySymbol(currency: string): string {
  try {
    const parts = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    const found = parts.find((p) => p.type === 'currency');
    return found?.value ?? currency;
  } catch {
    return currency;
  }
}
